import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { QuotationComposer } from './QuotationComposer';
import { STATUSES, fmtDate, normalizedStatus, statusLabel } from './leadUtils';
import { EnquiryCRM } from './EnquiryCRM';

// Detail dialog for a single lead. Mounted with key={lead.id} by the parent so
// all internal state resets when a different lead is opened.
export const LeadDetailDialog = ({ lead, onClose, authAxios, updateLead, onQuotationSent }) => {
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [aiData, setAiData] = useState(
    lead?.ai_summary ? { summary: lead.ai_summary, followups: lead.ai_followups || [] } : null
  );
  const [aiLoading, setAiLoading] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  if (!lead) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto break-words" aria-describedby={undefined} data-testid="admin-lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center justify-between gap-3 pr-6">
            <span>{lead.name}</span>
            <span className="font-mono-tech text-xs text-muted-foreground font-normal">{lead.lead_id}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <label className="text-xs font-mono-tech uppercase tracking-wider text-muted-foreground">Status</label>
            <Select value={normalizedStatus(lead.status)} onValueChange={(v) => updateLead(lead.id, { status: v }).catch(() => {})}>
              <SelectTrigger className="h-10 mt-1.5" data-testid="admin-lead-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`lead-status-${s.toLowerCase()}`}>{statusLabel(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <EnquiryCRM lead={lead} authAxios={authAxios} updateLead={updateLead} />
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm border border-border rounded-lg p-4 bg-muted/40">
            <dt className="text-muted-foreground">Phone</dt><dd data-testid="admin-lead-detail-phone">{lead.phone}</dd>
            <dt className="text-muted-foreground">Email</dt><dd>{lead.email || '\u2014'}</dd>
            <dt className="text-muted-foreground">Project type</dt><dd>{lead.project_type}</dd>
            <dt className="text-muted-foreground">Built-up area</dt><dd>{lead.built_up_area || '\u2014'}</dd>
            <dt className="text-muted-foreground">Floors</dt><dd>{lead.floors || '\u2014'}</dd>
            <dt className="text-muted-foreground">Location</dt><dd>{[lead.city, lead.district, lead.state].filter(Boolean).join(', ') || '\u2014'}</dd>
            <dt className="text-muted-foreground">Stage</dt><dd>{lead.project_stage || '\u2014'}</dd>
            <dt className="text-muted-foreground">Received</dt><dd>{fmtDate(lead.created_at)}</dd>
            <dt className="text-muted-foreground">Device</dt><dd>{lead.device_type || '\u2014'}</dd>
            <dt className="text-muted-foreground">Source</dt><dd>{lead.source || '\u2014'}</dd>
            <dt className="text-muted-foreground">UTM source</dt><dd>{lead.utm_source || '\u2014'}</dd>
            <dt className="text-muted-foreground">UTM medium</dt><dd>{lead.utm_medium || '\u2014'}</dd>
            <dt className="text-muted-foreground">UTM campaign</dt><dd>{lead.utm_campaign || '\u2014'}</dd>
            <dt className="text-muted-foreground">Landing page</dt><dd className="break-all">{lead.landing_page || '\u2014'}</dd>
          </dl>

          <div>
            <label className="text-xs font-mono-tech uppercase tracking-wider text-muted-foreground">AI Assistant</label>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={aiLoading}
                data-testid="admin-lead-ai-summary-button"
                onClick={async () => {
                  setAiLoading(true);
                  try {
                    const res = await authAxios.post(`/admin/leads/${lead.id}/ai-assist`, {}, { timeout: 90000 });
                    setAiData(res.data);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'AI summary failed');
                  } finally {
                    setAiLoading(false);
                  }
                }}
              >
                {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5 text-[#B87333]" />}
                AI Summary & Follow-ups
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={emailLoading || !lead.email}
                title={!lead.email ? 'This lead did not provide an email address' : undefined}
                data-testid="admin-lead-ai-email-button"
                onClick={async () => {
                  setEmailLoading(true);
                  try {
                    const res = await authAxios.post(`/admin/leads/${lead.id}/ai-email`, {}, { timeout: 90000 });
                    setEmailDraft({ subject: res.data.subject, body: res.data.body });
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'AI email draft failed');
                  } finally {
                    setEmailLoading(false);
                  }
                }}
              >
                {emailLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Mail className="w-3.5 h-3.5 mr-1.5 text-[#155E6D]" />}
                Draft Follow-up Email
              </Button>
              <QuotationComposer authAxios={authAxios} lead={lead} onSent={onQuotationSent} />
            </div>
            {!lead.email && (
              <p className="text-[11px] text-muted-foreground mt-1.5">Email drafting is unavailable — this lead only shared a phone number.</p>
            )}

            {aiData && (
              <div className="mt-3 rounded-lg border border-[#155E6D]/25 bg-[#155E6D]/[0.04] p-4 text-sm" data-testid="admin-lead-ai-summary-result">
                <p className="leading-relaxed">{aiData.summary}</p>
                {(aiData.followups || []).length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {aiData.followups.map((f, i) => (
                      <li key={`${i}-${f}`} className="flex items-start gap-2 text-muted-foreground">
                        <span className="font-mono-tech text-[10px] text-[#B87333] mt-1">{String(i + 1).padStart(2, '0')}</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {emailDraft && (
              <div className="mt-3 rounded-lg border border-border p-4 space-y-3" data-testid="admin-lead-ai-email-draft">
                <div>
                  <label className="text-xs font-medium">Subject</label>
                  <Input
                    value={emailDraft.subject}
                    onChange={(e) => setEmailDraft((d) => ({ ...d, subject: e.target.value }))}
                    className="h-9 mt-1"
                    data-testid="admin-lead-email-subject-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium">Body</label>
                  <Textarea
                    value={emailDraft.body}
                    onChange={(e) => setEmailDraft((d) => ({ ...d, body: e.target.value }))}
                    className="mt-1 min-h-[140px] text-sm"
                    data-testid="admin-lead-email-body-input"
                  />
                </div>
                <Button
                  size="sm"
                  disabled={sendingEmail || !emailDraft.subject.trim() || !emailDraft.body.trim()}
                  data-testid="admin-lead-email-send-button"
                  onClick={async () => {
                    setSendingEmail(true);
                    try {
                      await authAxios.post(`/admin/leads/${lead.id}/send-email`, emailDraft, { timeout: 60000 });
                      toast.success(`Email sent to ${lead.email}`);
                      setEmailDraft(null);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Email send failed — check SMTP settings');
                    } finally {
                      setSendingEmail(false);
                    }
                  }}
                >
                  {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
                  Send Email
                </Button>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-mono-tech uppercase tracking-wider text-muted-foreground">Internal Notes</label>
            <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
              {(lead.notes || []).length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
              {(lead.notes || []).map((n, i) => (
                <div key={`${n.created_at}-${i}`} className="text-sm bg-muted/60 rounded-lg p-3">
                  <p>{n.text}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{fmtDate(n.created_at)}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add an internal note…"
                className="min-h-[60px]"
                data-testid="admin-lead-note-input"
              />
            </div>
            <Button
              size="sm"
              className="mt-2"
              disabled={!noteText.trim() || savingNote}
              data-testid="admin-lead-add-note-button"
              onClick={async () => {
                setSavingNote(true);
                try {
                  await updateLead(lead.id, { note: noteText.trim() });
                  setNoteText('');
                } catch {
                  // The parent displays the API error; retain the unsaved note.
                } finally { setSavingNote(false); }
              }}
            >
              Add Note
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
